import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper, Alert } from '@mui/material';
import { ErrorOutline, Refresh, Home } from '@mui/icons-material';
import { generateErrorId } from '../../utils/idGenerator';

interface Props {
  readonly children?: ReactNode;
  readonly fallback?: ReactNode;
  readonly onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  readonly hasError: boolean;
  readonly error?: Error;
  readonly errorId?: string;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child component tree
 * Provides a fallback UI and error reporting capabilities
 * Follows WCAG 2.1 AA accessibility guidelines
 */
class ErrorBoundary extends Component<Props, State> {
  public override readonly state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Generate unique error ID for tracking
    const errorId = generateErrorId();

    return {
      hasError: true,
      error,
      errorId
    };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error details
    console.group('🚨 Error Boundary Caught Error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // In production, you might want to send this to an error reporting service
    if (import.meta.env.PROD) {
      // Example: Send to error tracking service
      // errorTrackingService.captureException(error, { 
      //   extra: errorInfo,
      //   tags: { errorId: this.state.errorId }
      // });
    }
  }

  private readonly handleReset = (): void => {
    this.setState({ 
      hasError: false
    });
  };

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  private readonly handleGoHome = (): void => {
    window.location.href = '/';
  };

  public override render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            padding: 3,
          }}
          role="alert"
          aria-labelledby="error-title"
          aria-describedby="error-description"
        >
          <Paper
            sx={{
              padding: 4,
              textAlign: 'center',
              maxWidth: 600,
            }}
          >
            <ErrorOutline
              color="error"
              sx={{ fontSize: 64, marginBottom: 2 }}
              aria-hidden="true"
            />
            
            <Typography 
              id="error-title"
              variant="h5" 
              gutterBottom
            >
              Something went wrong
            </Typography>
            
            <Typography 
              id="error-description"
              variant="body1" 
              color="textSecondary" 
              paragraph
            >
              We encountered an unexpected error. This has been logged and our team will investigate.
              You can try the actions below to continue using the application.
            </Typography>

            {/* Error ID for support purposes */}
            {this.state.errorId && (
              <Alert 
                severity="info" 
                sx={{ mt: 2, mb: 3, textAlign: 'left' }}
              >
                <Typography variant="caption">
                  Error ID: <code>{this.state.errorId}</code>
                </Typography>
              </Alert>
            )}

            {/* Development error details */}
            {import.meta.env.DEV && this.state.error && (
              <Box 
                sx={{ 
                  mt: 3, 
                  mb: 3,
                  p: 2, 
                  bgcolor: 'grey.100', 
                  textAlign: 'left',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.300'
                }}
              >
                <Typography variant="subtitle2" gutterBottom color="error">
                  Development Error Details:
                </Typography>
                <Typography 
                  variant="caption" 
                  component="pre" 
                  sx={{ 
                    overflow: 'auto',
                    maxHeight: '200px',
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {this.state.error.name}: {this.state.error.message}
                  {this.state.error.stack && `\n\nStack trace:\n${this.state.error.stack}`}
                </Typography>
              </Box>
            )}

            {/* Action buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                onClick={this.handleReset}
                startIcon={<Refresh />}
                sx={{ minWidth: '120px', minHeight: '44px' }}
              >
                Try Again
              </Button>
              <Button 
                variant="outlined" 
                onClick={this.handleReload}
                startIcon={<Refresh />}
                sx={{ minWidth: '120px', minHeight: '44px' }}
              >
                Reload Page
              </Button>
              <Button 
                variant="text" 
                onClick={this.handleGoHome}
                startIcon={<Home />}
                sx={{ minWidth: '120px', minHeight: '44px' }}
              >
                Go Home
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;